import { MathUtils } from 'three'
import { Mesh, Plane, Program, RenderTarget, Transform, Camera, Vec2, Vec3, Orbit, Color, Raycast } from 'ogl'
import gsap from '@/lib/gsap'
import { ScrollTrigger } from '@/lib/gsap'
import State from '@/lib/hey'

import { blendOverlay, add, rotateUV, hardLight } from '../../Utils/ShaderChunks'

import Gl from '../../Gl'

import Background from './Modules/Background'
import Glass from './Modules/Glass'
import Target from './Modules/Target'

export default class ScenePostProcessing {
  constructor(_params) {
    gsap.registerPlugin(ScrollTrigger)

    /* 
      Setup
    */
    this.id = 'post-processing'

    /* 
      Params
    */
    this.params = _params

    /* 
      Flags
    */
    this.isRendering = true
    this.isDesktop = window.innerWidth > 991 ? 1 : 0

    /* 
      GL
    */
    this.gl = new Gl()

    /* 
      Render Plane
    */
    this.renderPlane = {
      mesh: new Mesh(this.gl.renderer.instance.gl, {
        //
        // mode: this.gl.renderer.instance.gl.LINES,
        geometry: new Plane(this.gl.renderer.instance.gl, {
          width: 1,
          height: 1,
        }),
        program: new Program(this.gl.renderer.instance.gl, {
          transparent: true,
          uniforms: {
            // tForeground: { value: null },
            tObjectDefault: { value: null },
            tObjectGhosted: { value: null },
            tBackground: { value: null },

            tMatcap: { value: this.gl.assets.textures.matcaps.glass },
            tNoise: { value: this.gl.assets.textures.noise },
            tGlassReflection: { value: this.gl.assets.textures.glassReflection },

            uScale: { value: new Vec2(this.gl.sizes.width, this.gl.sizes.height) },
            uResolution: { value: new Vec2(this.gl.sizes.width, this.gl.sizes.height) },
            uPosition: { value: new Vec2(0, 0) },
            uAspect: { value: this.gl.sizes.aspect },

            uRefractionStrengthObject: { value: 0.1 },
            uRefractionStrength1: { value: 0.015 },
            uRefractionNoiseDistortionStrength: { value: 3 },
            uRefractionNoiseDensity: { value: 9.0 },

            uDecayOpacity: { value: 0.9 },

            uReveal: { value: 0.0 },
          },
          vertex: /* glsl */ `
            attribute vec2 uv;
            attribute vec3 position;
            attribute vec3 normal;

            varying vec2 vUv;
            varying vec3 vNormal;
            varying vec4 vViewPosition;

            uniform mat4 projectionMatrix;
            uniform mat4 modelViewMatrix;
            uniform mat3 normalMatrix;

            uniform vec2 uPosition;
            uniform vec2 uScale;
            uniform vec2 uResolution;

            void main() {
              vec2 pos = position.xy * 2.0;

              // Scale
              pos.x *= uScale.x / uResolution.x;
              pos.y *= uScale.y / uResolution.y;

              // Position
              pos.x += - 1.0 + uPosition.x / uResolution.x * 2. + uScale.x / uResolution.x;
              pos.y -= uPosition.y / uResolution.y * 2.0;
              
              gl_Position = vec4(pos.xy, 0.0, 1.0);
            
              // Varyings
              vUv = uv;
            }
          `,
          fragment: /* glsl */ `
            precision highp float;
           
            // uniform sampler2D tForeground;
            uniform sampler2D tObjectDefault;
            uniform sampler2D tObjectGhosted;
            uniform sampler2D tBackground;

            uniform sampler2D tMatcap;
            uniform sampler2D tNoise;
            uniform sampler2D tGlassReflection;

            uniform float uRefractionStrengthObject;
            uniform float uRefractionStrength1;
            uniform float uRefractionNoiseDistortionStrength;
            uniform float uRefractionNoiseDensity;

            uniform float uDecayOpacity;

            uniform float uReveal;

            uniform float uAspect;
            uniform vec2 uResolution;

            varying vec2 vUv;

            ${add}
            ${rotateUV}
            ${blendOverlay}
            ${hardLight}

            void main() {
              /* 
                UV
              */
              vec2 uvN = vec2(vUv.x * uAspect, vUv.y);

              /* 
                Foreground Texture
              */
              // vec4 textureForeground = texture2D(tForeground, vUv);

              /* 
                Noise
              */
              float textureNoise = texture2D(tNoise, vec2(uvN.x, 0.0)).r;

              /* 
                Matcap
              */
              // vec2 matcapUv = textureForeground.rgb.xy * 0.5 + 0.5; // 0.495 to remove artifacts caused by undersized matcap disks
              // vec3 matcap = texture2D(tMatcap, matcapUv).rgb;

              /* 
                Fresnel
              */
              // float fresnel = 1.0 - abs(dot(normalize(textureForeground.rgb), normalize(vec3(0.0, 0.0, -1.0))));
              // fresnel = pow(fresnel, 2.0);

              /* 
                Refraction
              */
              float refraction = fract(uvN.x * uRefractionNoiseDensity + textureNoise * uRefractionNoiseDistortionStrength);
              // float refractionColorDetail = fract(vUv.x * uRefractionNoiseDensity + textureNoise * uRefractionNoiseDistortionStrength);
              
              /* 
                Blurry Objects
              */
              // vec3 refractedBlurryObject = refract(vec3(0.0, 0.0, -1.0), textureForeground.rgb, uRefractionStrengthObject);
              // vec2 uvRefractedBlurryObject = vUv + refractedBlurryObject.xy;
              float refraction0 = refraction * uRefractionStrengthObject;
              vec2 uvRefractedBlurryObject = vUv + vec2(refraction0, 0.0);

              // vec2 uvBlurryObject0 = rotateUV((uvRefractedBlurryObject + vec2(-0.4, 1.0)) * 0.33 + uBlurryObjectsOffset, radians(-45.0), vec2(0.5, 0.5));
              // vec2 uvBlurryObject1 = rotateUV((uvRefractedBlurryObject + vec2(-0.4, 0.75)) * 0.6 + uBlurryObjectsOffset, radians(-45.0), vec2(0.5, 0.5));

              // float blurryObjectTexture0 = texture2D(tBlurryObject0, uvBlurryObject0).a;
              // float blurryObjectTexture1 = texture2D(tBlurryObject1, uvBlurryObject1).a;

              // vec4 blurryObject0 = vec4(pow(COLOR_OBJECT_0, vec3(2.2)), blurryObjectTexture0);
              // vec4 blurryObject1 = vec4(pow(COLOR_OBJECT_1, vec3(2.2)), blurryObjectTexture1);

              // vec4 blurryObjects = add(blurryObject1, blurryObject0);
              vec4 textureBlurryObjects = texture2D(tBackground, uvRefractedBlurryObject);

              /* 
                Refraction
              */
              // vec3 refractedMainObject = refract(vec3(0.0, 0.0, -1.0), textureForeground.rgb, uRefractionStrength1);
              float refraction1 = refraction * uRefractionStrength1;
              vec2 uvRefractedMainObject = vUv + vec2(refraction1, 0.0);
              
              /* 
                Background Texture
              */
              vec4 textureObjectDefault = texture2D(tObjectDefault, uvRefractedMainObject);
              vec4 textureObjectGhosted = texture2D(tObjectGhosted, uvRefractedMainObject);
              textureObjectGhosted.a *= 1.0 - refraction;

              vec4 object = mix(textureObjectDefault, vec4(textureObjectGhosted.rgb, textureObjectGhosted.a * uDecayOpacity), 1.0 - textureObjectDefault.a);

              /* 
                Background
              */
              // vec3 background = mix(vec3(1.0), textureObjectGhosted.rgb, textureObjectGhosted.a);
              
              /* 
                Glass Reflection
              */
              float textureGlassReflection = texture2D(tGlassReflection, vec2(refraction, vUv.y)).r;

              /* 
                Color
              */
              vec4 color = add(object, textureBlurryObjects);
              // color.rgb += fresnel + matcap;

              /* 
                Light
              */
              // float light = 1.0 - max(dot(textureForeground.rgb, vec3(1.0, 0.0, 1.0)), 0.0);
              // gl_FragColor.rgb = blendHardLight(color.rgb, vec3(textureGlassReflection), 0.2);
              gl_FragColor.rgb = mix(vec3(1.0), blendHardLight(color.rgb, vec3(textureGlassReflection), 0.2), color.a * uReveal);
              gl_FragColor.a = 1.0;
              // gl_FragColor = textureBlurryObjects;
              
              // Debug
              // gl_FragColor.rgb += vec3(vUv.x, vUv.y, 0.0);
              // gl_FragColor.a = 1.0;
            }
          `,
        }),
      }),
    }

    this.renderPlane.mesh.frustumCulled = false
    this.renderPlane.mesh.matrixAutoUpdate = false

    /* 
      Bounds
    */
    this.bounds = {}

    /* 
      Camera
    */
    this.camera = new Camera(this.gl.renderer.instance.gl)
    this.camera.perspective({
      fov: 32,
      aspect: this.gl.sizes.aspect,
      near: 0.1,
      far: 100,
    })
    this.camera.position.z = 35 / 1.75
    // this.camera.position.x = 13.5 / 1.5
    // this.camera.position.y = 3.95 / 3

    /* 
      Controls
    */
    if (this.gl.isDebug) {
      this.controls = new Orbit(this.camera, {
        target: new Vec3(0, 0, 0),
      })
    }

    /* 
      Modules
    */

    this.background = new Background({})
    this.glass = new Glass({
      camera: this.camera,
    })
    this.target = new Target({
      camera: this.camera,
    })
    // this.glass.scene.setParent(this.gl.scene)

    /* 
      Raycaster
    */
    this.raycaster = new Raycast()
    this.raycastedPosition = {
      current: new Vec3(0, 0, 0),
      target: new Vec3(0, 0, 0),
      ease: 0.025,
      multiplyObjectPosition: 1,
      multiplyBlurPosition: 0.005,
    }

    this.setDefault()

    /* 
      Eased Mouse
    */
    this.easedMouse = {
      normalized: this.gl.mouse.createEasedNormalized(0.025),
      pace: this.gl.mouse.createEasedPace(0.025),
    }

    /* 
      Functions
    */
    // this.setEvents()
    this.reveal()
    if (this.gl.state.data) this.setVariants() // Handle default variants
    if (this.gl.isDebug) this.setDebug()

    /* 
      Events
    */
    State.on('WEBGL_DATA', (data) => {
      this.gl.state.data = data

      this.setVariants()
    })
  }

  reveal() {
    gsap.to(this.renderPlane.mesh.program.uniforms.uReveal, {
      value: 1.0,
      duration: 1.0,
      ease: 'power1.inOut',
      delay: 0.5,
    })
  }

  setDefault() {
    if (window.innerWidth > 991) {
      this.default = {
        position: new Vec3(5, 1, 0),
        multiply: new Vec3(5, 3, 0),
        multiplayRotation: new Vec3(5, 10, 0),
      }
    } else {
      this.default = {
        position: new Vec3(0.25, -2.25, 0),
        multiply: new Vec3(0.75, 0.75, 0),
        multiplayRotation: new Vec3(0, 0, 0),
      }
    }
  }

  resize() {
    this.background.resize()
    this.glass.resize()
    this.target.resize()

    this.renderPlane.mesh.program.uniforms.uResolution.value.set(this.gl.sizes.width, this.gl.sizes.height)
    this.renderPlane.mesh.program.uniforms.uScale.value.set(this.gl.sizes.width, this.gl.sizes.height)
    this.renderPlane.mesh.program.uniforms.uAspect.value = this.gl.sizes.aspect

    this.camera.perspective({
      aspect: this.gl.sizes.aspect,
    })

    this.setDefault()

    this.isDesktop = window.innerWidth > 991 ? 1 : 0
  }

  setVariants() {
    this.target.setActiveObject(this.gl.state.data.model)
    this.background.setColor(this.gl.state.data.color)
    this.target.setColor(this.gl.state.data.color, this.gl.state.data.color == 4 ? 0.33 : 1)

    /*
      Set model visiblity
    */
    if (this.gl.state.data.scene === 'full-with-model') {
      this.target.setVisible(true)
    } else {
      this.target.setVisible(false)
    }

    /*
      Set refraction strength
    */
    if (this.gl.state.data.scene === 'bottom-soft') {
      this.renderPlane.mesh.program.uniforms.uRefractionNoiseDensity.value = 5
      this.renderPlane.mesh.program.uniforms.uRefractionNoiseDistortionStrength.value = 4
    } else {
      this.renderPlane.mesh.program.uniforms.uRefractionNoiseDensity.value = 9
      this.renderPlane.mesh.program.uniforms.uRefractionNoiseDistortionStrength.value = 3
    }

    /*
      Set background variant
    */
    this.background.setVariant(this.gl.state.data.scene)
  }

  setDebug() {
    /* 
      Scene Params
    */
    this.gl.world.debugSceneParamsFolder
      .addBinding({ value: 'mouse' }, 'value', {
        label: 'Object',
        options: {
          mouse: 'mouse',
          star: 'star',
          code: 'code',
          warning: 'warning',
          compass: 'compass',
          cards: 'cards',
        },
      })
      .on('change', (_e) => {
        this.target.setActiveObject(_e.value)
      })

    this.gl.world.debugSceneParamsFolder
      .addBinding({ value: 0 }, 'value', {
        label: 'Background Color',
        options: {
          'Blue / Baby Pink': 0,
          'Purple / Pink': 1,
          'Red / Orange': 2,
          'Green / Pink': 3,
          'Blue / Orange': 4,
          'Blue / Pink': 5,
        },
      })
      .on('change', (_e) => {
        this.background.setColor(_e.value)
        this.target.setColor(_e.value, _e.value == 4 ? 0.33 : 1)
      })

    this.gl.world.debugSceneParamsFolder
      .addBinding({ value: 'full-with-model' }, 'value', {
        label: 'Scene Variant',
        options: {
          'Full with model': 'full-with-model',
          'Full without model': 'full-without-model',
          'Bottom full': 'bottom_full',
          'Bottom soft': 'bottom_soft',
        },
      })
      .on('change', (_e) => {
        /* 
          Set model visiblity
        */
        if (_e.value === 'full-with-model') {
          this.target.setVisible(true)
        } else {
          this.target.setVisible(false)
        }

        /* 
          Set refraction strength
        */
        if (_e.value === 'bottom_soft') {
          this.renderPlane.mesh.program.uniforms.uRefractionNoiseDensity.value = 5
          this.renderPlane.mesh.program.uniforms.uRefractionNoiseDistortionStrength.value = 4
        } else {
          this.renderPlane.mesh.program.uniforms.uRefractionNoiseDensity.value = 9
          this.renderPlane.mesh.program.uniforms.uRefractionNoiseDistortionStrength.value = 3
        }

        /* 
          Set background variant
        */
        this.background.setVariant(_e.value)
      })

    /* 
      Refraction
    */
    this.refractionFolder = this.gl.world.debugWorldFolder.addFolder({
      title: 'Refraction',
    })

    this.refractionFolder.addBinding(this.renderPlane.mesh.program.uniforms.uRefractionStrengthObject, 'value', {
      label: 'Background',
      min: 0.0,
      max: 1.0,
      step: 0.001,
    })

    this.refractionFolder.addBinding(this.target.active.program.uniforms.uRefractionStrength, 'value', {
      label: 'Background inside object',
      min: 0.0,
      max: 1.0,
      step: 0.001,
    })

    this.refractionFolder.addBinding(this.renderPlane.mesh.program.uniforms.uRefractionStrength1, 'value', {
      label: 'Object',
      min: 0.0,
      max: 1.0,
      step: 0.001,
    })

    this.refractionFolder.addBinding(this.renderPlane.mesh.program.uniforms.uRefractionNoiseDistortionStrength, 'value', {
      label: 'Noise Distortion Strength',
      min: 0.1,
      max: 10.0,
      step: 0.01,
    })

    this.refractionFolder.addBinding(this.renderPlane.mesh.program.uniforms.uRefractionNoiseDensity, 'value', {
      label: 'Noise Density',
      min: 0.1,
      max: 100.0,
      step: 0.01,
    })

    // this.camera.position.x
    /* 
      Camera
    */
    this.cameraFolder = this.gl.world.debugWorldFolder.addFolder({
      title: 'Camera',
    })
    this.cameraFolder.addBinding(this.camera.position, 'x', {
      label: 'Camera Position X',
    })

    /* 
      Blur
    */
    this.blurFolder = this.gl.world.debugWorldFolder.addFolder({
      title: 'Blur',
    })

    this.blurFolder.addBinding(this.target.blur.hPlane.program.uniforms.uBlurStrength, 'value', {
      label: 'Blur Strength',
    })

    this.blurFolder.addBinding(this.target.blur.hPlane.program.uniforms.uMinimalBlurStrength, 'value', {
      label: 'Minimal Blur Strength',
    })

    this.blurFolder.addBinding(this.target.blur.hPlane.program.uniforms.uBlurStart, 'value', {
      label: 'Blur Start',
      min: 0.0,
      max: 1.0,
      step: 0.001,
    })

    this.blurFolder.addBinding(this.target.blur.hPlane.program.uniforms.uBlurEnd, 'value', {
      label: 'Blur End',
      min: 0.0,
      max: 1.0,
      step: 0.001,
    })

    /* 
      Decay
    */
    this.decayFolder = this.gl.world.debugWorldFolder.addFolder({
      title: 'Decay',
    })

    this.decayFolder.addBinding(this.target.feedback.plane.program.uniforms.uDecay, 'value', {
      label: 'Strength',
      min: 0.0,
      max: 1.0,
      step: 0.001,
    })

    this.decayFolder.addBinding(this.renderPlane.mesh.program.uniforms.uDecayOpacity, 'value', {
      label: 'Opacity',
      min: 0.0,
      max: 1.0,
      step: 0.001,
    })
  }

  setEvents() {
    document.addEventListener('mousemove', () => {
      const position = this.getSpatialPosition()
      this.raycastedPosition.target.set(
        //
        position.x * this.raycastedPosition.multiplyObjectPosition,
        position.y * this.raycastedPosition.multiplyObjectPosition,
        position.z * this.raycastedPosition.multiplyObjectPosition
      )
    })
  }

  getSpatialPosition(_customPosition = false) {
    if (_customPosition) {
      this.raycaster.castMouse(this.camera, [_customPosition.x, _customPosition.y])
    } else {
      this.raycaster.castMouse(this.camera, [this.gl.mouse.normalized.current.x, this.gl.mouse.normalized.current.y])
    }

    const hits = this.raycaster.intersectBounds(this.glass.glass.raycastingMesh.children[0])

    return hits[0].hit.point
  }

  renderPipeline() {
    if (!this.isRendering) return

    this.background.renderPipeline()

    if (this.target.active) {
      this.target.active.program.uniforms.tBackground.value = this.background.renderTarget.texture
    }

    this.glass.renderPipeline()
    this.target.renderPipeline()

    this.renderPlane.mesh.program.uniforms.tBackground.value = this.background.renderTarget.texture
    // this.renderPlane.mesh.program.uniforms.tForeground.value = this.glass.renderTarget.texture
    this.renderPlane.mesh.program.uniforms.tObjectDefault.value = this.target.sceneRenderTarget.texture
    this.renderPlane.mesh.program.uniforms.tObjectGhosted.value = this.target.feedbackRenderTargetB.texture
  }

  update() {
    if (!this.isRendering) return
    this.easedMouse.normalized.update(this.gl.time.delta)
    this.easedMouse.pace.update(this.gl.time.delta)

    this.background.update()
    this.glass.update()
    this.target.update()

    /* 
      Background
    */
    if (this.background.active.mesh.program.uniforms.uBlurryObjectsOffset0) {
      this.background.active.mesh.program.uniforms.uBlurryObjectsOffset0.value.set(
        //
        -this.easedMouse.normalized.value.x * 0.05,
        -this.easedMouse.normalized.value.y * 0.05
      )
      this.background.active.mesh.program.uniforms.uBlurryObjectsOffset1.value.set(
        //
        -this.easedMouse.normalized.value.x * 0.025,
        -this.easedMouse.normalized.value.y * 0.025
      )
    }

    this.background.instance1.mesh.program.uniforms.uMousePosition.value.set(
      //
      this.easedMouse.normalized.value.x,
      this.easedMouse.normalized.value.y
    )

    /* 
      Target
    */
    if (this.target.active) {
      this.target.active.position.set(
        //
        this.default.position.x + this.easedMouse.normalized.value.x * this.default.multiply.x,
        this.default.position.y + this.easedMouse.normalized.value.y * this.default.multiply.y + Math.sin(this.gl.time.elapsed) * 0.2,
        this.default.position.z
      )

      this.target.active.rotation.x = this.easedMouse.pace.value.separated.y * this.default.multiplayRotation.y
      this.target.active.rotation.z = -this.easedMouse.pace.value.separated.x * this.default.multiplayRotation.x
    }

    /* 
      Camera
    */
    this.camera.lookAt([0, 0, 0])

    if (this.gl.isDebug && this.gl.world.isControlsAllowed) {
      this.controls.update()
    }
  }
}
